import { Link, useNavigate, useOutletContext } from "react-router-dom";
import { ReactComponent as ContainersLogoSVG } from "../assets/images/container-icon.svg";
import { ReactComponent as LinkIconSVG } from '../assets/images/link-icon.svg';
import { ReactComponent as MailCircleSVG } from '../assets/images/mail-circle-icon.svg';
import { ReactComponent as OutlinedStarSVG } from "../assets/images/star-outline.svg";
import { ReactComponent as FilledStarSVG } from "../assets/images/star-filled.svg";
import { ReactComponent as StatusErrorSVG } from '../assets/images/status-error.svg';
import LoadingMessage from "../components/LoadingMessage";
import ErrorMessage from "../components/ErrorMessage";
import Guides from "../components/Guides";
import { DsButton, Eyebrow, StatusPill, SurfaceCard, TileRow } from "../components/ds";
import { PlusIcon, SearchIcon } from "../components/ds/icons";
import { TOAST_SURFACE } from "../components/ds/toast";
import { App, AuthenticationError, AuthorizationError, NetworkError, NotFoundError, Subscription, User } from "@back4app2/sdk";
import { useEffect, useReducer } from "react";
import back4app2 from "../back4app2";
import { BACK4APP_DOT_COM_SITE_URL } from "../settings";
import { AmplitudeEvent, trackEvent } from "../utils/amplitude";
import { AppStatus } from "@back4app2/sdk";
import { toast } from "react-hot-toast";
import TextInput from "../components/FormInputField/TextInput/TextInput";
import MyAppStatus from "../components/MyAppStatus";
import { debounceFunction } from "../utils";

interface MyAppsState {
  isLoading: boolean;
  loadingErrorMessage?: string;
  apps?: App[];
  filteredApps?: App[];
  searchText: string;
}

const INITIAL_STATE: MyAppsState = {
  isLoading: true,
  searchText: ''
};

enum MyAppsActionType {
  RESET,
  FINISH_LOADING,
  SET_FILTERED_APPS
}

const reset = () => ({
  type: MyAppsActionType.RESET
} as const);

const finishLoading = (errorMessage?: string, apps?: App[]) => ({
  type: MyAppsActionType.FINISH_LOADING,
  payload: {
    errorMessage,
    apps
  }
} as const);

const setFilteredApps = (searchText: string) => ({
  type: MyAppsActionType.SET_FILTERED_APPS,
  payload: {
    searchText,
  }
} as const);

type MyAppsAction = ReturnType<typeof reset> | ReturnType<typeof finishLoading> | ReturnType<typeof setFilteredApps>;

const reducer = (state: MyAppsState = INITIAL_STATE, action: MyAppsAction): MyAppsState => {
  const getFilteredApps = (apps?: App[], searchText?: string) => {
    let filteredApps = (apps && [...apps]) || [];
    if (searchText) {
      const regex = new RegExp(`${searchText}`, 'ig');
      filteredApps = filteredApps.filter(app => app.name.match(regex)) || [];
    }
    filteredApps = filteredApps.sort((a, b) => b.isFavourite === a.isFavourite ? a.name.localeCompare(b.name) : +b.isFavourite - +a.isFavourite);
    return filteredApps;
  };

  switch (action.type) {
    case MyAppsActionType.RESET:
      return INITIAL_STATE;
    
    case MyAppsActionType.FINISH_LOADING:
      return {
        ...state,
        isLoading: false,
        loadingErrorMessage: action.payload.errorMessage,
        apps: action.payload.apps,
        filteredApps: getFilteredApps(action.payload.apps, state.searchText)
      };

    case MyAppsActionType.SET_FILTERED_APPS: {
      
      return {
        ...state,
        filteredApps: getFilteredApps(state.apps, action.payload.searchText),
        searchText: action.payload.searchText
      };
    }
  }
};

const MyApps = () => {
  const { freePlanUsageHours, user } = useOutletContext<{ freePlanUsageHours: number, user: User }>();
  const [state, dispatch] = useReducer(reducer, INITIAL_STATE);
  let { isLoading, loadingErrorMessage, apps, filteredApps, searchText } = state;
  const navigate = useNavigate();

  useEffect(
    () => {
      let subscription: Subscription;

      subscription = back4app2.subscribeToApps(
        (error, snapshot) => {
          if (error) {
            if (error instanceof NetworkError) {
              console.error('network error', error);
              dispatch(finishLoading('Network error when loading apps. Check your internet connection and try again.'));
            } else if (error instanceof AuthenticationError || error instanceof AuthorizationError) {
              window.location.replace(`${BACK4APP_DOT_COM_SITE_URL}/login?return-url=${encodeURIComponent(window.location.href)}`);
            } else {
              console.error('unexpected error loading apps', error);
              dispatch(finishLoading('Unexpected error when loading apps. Please try again.'));
            }
          } else {
            dispatch(finishLoading(undefined, snapshot));
          }
        }
      );

      return () => {
        if (subscription) {
          subscription.unsubscribe();
          dispatch(reset());
        }
      };
    },
    []
  );

  useEffect(() => {
    trackEvent(AmplitudeEvent.AT_CONTAINERS_DASHBOARD_PAGE);
  }, []);

  const handleToggleAppFavourite = async (appId: string) => {
    try {
      const selectedApp = apps?.find(app => app.id === appId);
      if (!selectedApp) {
        return;
      }
      await back4app2.updateAppFavourite(selectedApp.id, !selectedApp.isFavourite);
    } catch (err) {
      let errMsg = 'Something went wrong!';
      if (err instanceof NetworkError) {
        console.error('network error', err);
        errMsg = 'Network error when updating app favourites. Check your internet connection and try again.'
      } else if (err instanceof AuthenticationError || err instanceof AuthorizationError) {
        window.location.replace(`${BACK4APP_DOT_COM_SITE_URL}/login?return-url=${encodeURIComponent(window.location.href)}`);
      } else if (err instanceof NotFoundError) {
        errMsg = 'App not found!';
      } else {
        console.log('error while updating favourites ', err);
      }
      toast(errMsg, {
        className: TOAST_SURFACE,
        duration: 3 * 1000,
        icon: <StatusErrorSVG width="24px" height="24px" className="animate-bounce-in" />
      });
    }
  }

  const favouriteAppsCount = filteredApps?.filter(app => app.isFavourite).length || 0;
  const freeAppsCount = apps
    ? apps.filter(app => app.mainService?.mainServiceEnvironment?.plan?.name?.includes('Free') && app.isOwner).length
    : undefined;
  const quotaLabel = `${freeAppsCount ?? '—'}${user?.maxApps ? ` / ${user.maxApps}` : ''}`;

  const startDeploy = () => {
    if (state.apps?.some(app => app.status === AppStatus.PENDING_VERIFICATION)) {
      toast('Please complete email verification!', {
        className: TOAST_SURFACE,
        duration: 3 * 1000,
        icon: <MailCircleSVG className="text-error-red" width="22px" height="22px" />
      });
      return;
    }
    navigate('/new-container');
  };

  const hasApps = !!apps && apps.length > 0;

  return (<div className="flex-1 min-h-0 overflow-y-auto b4-thin bg-navy-900">
    <div className="mx-auto w-full max-w-[1120px] px-4 lg:px-10 pt-7 pb-9 flex flex-col gap-5">

      <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between lg:gap-8">
        <div className="flex flex-col gap-2">
          <div className="flex items-center gap-2">
            <StatusPill tone="info">Beta</StatusPill>
            <Eyebrow>web applications</Eyebrow>
          </div>
          <h1 className="font-sora text-ds-20 font-semibold tracking-[-0.02em] leading-[1.2] text-white">
            Manage your web applications
          </h1>
          <p className="text-ds-135 leading-[1.5] text-navy-200">
            Explore your existing applications or kickstart a new project.
          </p>
        </div>
        <div className="hidden lg:flex items-center gap-3">
          <div className="flex items-center gap-2.5 h-[38px] px-3.5 rounded-surface border border-navy-500 bg-navy-700">
            <span className="text-[13px] text-navy-200">Free web applications</span>
            <span className="font-courrier text-ds-125 text-white bg-navy-600 border border-navy-500 rounded-full px-2.5 py-[3px] leading-none">
              {quotaLabel}
            </span>
          </div>
          <DsButton variant="primary" size="lg" onClick={startDeploy}>
            <PlusIcon size={15} />
            Deploy a web app
          </DsButton>
        </div>
      </div>

      {/* On mobile the quota is a card of its own — it does not fit beside the
          title, and it is the number people come back to check. */}
      <SurfaceCard className="lg:hidden px-4 py-3.5 flex items-center justify-between gap-3">
        <div className="flex flex-col gap-1.5 min-w-0">
          <Eyebrow>free quota</Eyebrow>
          <span className="text-ds-135 text-navy-100">Free web applications</span>
        </div>
        <span className="flex-none font-courrier text-ds-125 text-white bg-navy-600 border border-navy-500 rounded-full px-3 py-[5px] leading-none">
          {quotaLabel}
        </span>
      </SurfaceCard>

      {isLoading ? (
        <SurfaceCard tone="surface" className="min-h-[20rem] flex items-center">
          <LoadingMessage message="Loading apps" />
        </SurfaceCard>
      ) : loadingErrorMessage ? (
        <ErrorMessage message={loadingErrorMessage} />
      ) : !hasApps ? (
        <EmptyApps onDeploy={startDeploy} />
      ) : (
        <>
          {/* The green CTA lives in the header on desktop; on mobile it sits
              here, where the thumb is. */}
          <DsButton variant="primary" size="touch" onClick={startDeploy} className="lg:hidden w-full">
            <PlusIcon size={15} />
            Deploy a web app
          </DsButton>

          <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
            <Eyebrow>{`${apps!.length} web application${apps!.length === 1 ? '' : 's'}`}</Eyebrow>
            <TextInput
              value={searchText}
              onChange={(e) => dispatch(setFilteredApps(e.target.value))}
              placeholderText="Search"
              className="md:min-w-[18rem]"
              endAdornment={<SearchIcon size={16} className="text-navy-300" />}
            />
          </div>

          {!filteredApps || filteredApps.length <= 0 ? (
            <SurfaceCard tone="surface" className="px-4 py-6 text-ds-135 text-navy-200 text-center">
              No application matches that search.
            </SurfaceCard>
          ) : (
            <div className="flex flex-col gap-3">
              {filteredApps.map((app, idx) => (
                <div key={app.id}>
                  <SurfaceCard className="px-4 py-3.5 flex flex-col gap-2.5">
                    <div className="flex items-center justify-between gap-3">
                      <div className="flex items-center gap-2.5 min-w-0">
                        <div className="w-11 h-11 flex-none rounded-surface bg-navy-600 border border-navy-500 flex items-center justify-center text-state-ok-soft">
                          <ContainersLogoSVG width="20px" height="20px" />
                        </div>
                        <div className="min-w-0 flex flex-col gap-1">
                          <Link
                            to={`/apps/${app.id}`}
                            className="b4-focus text-ds-135 font-semibold text-white truncate hover:text-action-soft"
                          >
                            {app.name}
                          </Link>
                          {/* Hidden while the app is blocked: a row that still
                              links out is an invitation to a resource under
                              review. */}
                          {app.mainService?.mainServiceEnvironment?.mainCustomDomain?.name && !app.block ? (
                            <a
                              target="_blank"
                              rel="noreferrer"
                              href={`${window.location.protocol}//${app.mainService.mainServiceEnvironment.mainCustomDomain.name}`}
                              className="b4-focus inline-flex items-center gap-1.5 min-w-0 font-courrier text-ds-115 text-navy-300 hover:text-action-soft"
                            >
                              <span className="truncate">{app.mainService.mainServiceEnvironment.mainCustomDomain.name}</span>
                              <LinkIconSVG className="flex-none" />
                            </a>
                          ) : null}
                        </div>
                      </div>
                      <div className="flex items-center gap-3 flex-none">
                        {app.mainService?.mainServiceEnvironment?.plan
                          ? <AppPlanChip planName={app.mainService.mainServiceEnvironment.plan.name || ''} />
                          : null}
                        <button
                          type="button"
                          aria-label={app.isFavourite ? 'Remove from favourites' : 'Add to favourites'}
                          className="b4-focus rounded-control p-1"
                          onClick={() => {
                            const debouncedFunction = debounceFunction(handleToggleAppFavourite, 500);
                            debouncedFunction(app.id);
                          }}
                        >
                          {app.isFavourite ? <FilledStarSVG /> : <OutlinedStarSVG />}
                        </button>
                      </div>
                    </div>
                    <div className="flex justify-end items-center border-t border-navy-500 pt-2.5">
                      <MyAppStatus app={app} />
                    </div>
                  </SurfaceCard>
                  {(favouriteAppsCount - 1 === idx) && favouriteAppsCount !== filteredApps?.length
                    ? <hr className="border-dashed border-navy-500 mt-3" />
                    : null}
                </div>
              ))}
            </div>
          )}
        </>
      )}

      <Guides />
    </div>
  </div>);
};

/**
 * The old empty state was a ghost and the words "Nothing here, yet!" filling
 * 300px — it said nothing about what to do next. This one names the three
 * steps and ends on the button that starts them.
 */
const EmptyApps = ({ onDeploy }: { onDeploy: () => void }) => {
  const steps = [
    { n: '01', eyebrow: 'source', value: 'Pick a GitHub repository' },
    { n: '02', eyebrow: 'build', value: 'Back4app builds the image' },
    { n: '03', eyebrow: 'live', value: 'You get a URL and logs' },
  ];

  return (
    <SurfaceCard tone="surface" className="p-5 lg:px-8 lg:py-7 grid gap-4 lg:grid-cols-[minmax(0,1fr)_440px] lg:gap-11 lg:items-center">
      <div className="flex flex-col gap-2.5">
        <span className="font-sora text-ds-15 lg:text-ds-20 font-semibold tracking-[-0.02em] text-white">
          No web apps yet
        </span>
        <p className="text-ds-135 leading-[1.55] text-navy-200 max-w-[54ch]">
          Connect a Git repository and Back4app builds the image, runs the container, and hands you a URL.
        </p>
        <div className="hidden lg:flex items-center gap-4 mt-1.5">
          <DsButton variant="action" size="lg" onClick={onDeploy}>Connect a repository</DsButton>
          <a
            href="https://www.back4app.com/docs-containers"
            target="_blank"
            rel="noreferrer"
            className="b4-focus text-[13px] font-semibold text-action-soft hover:text-white"
          >
            Read the guide
          </a>
        </div>
      </div>
      <div className="rounded-card border border-navy-500 bg-navy-700 overflow-hidden">
        {steps.map((step, i) => (
          <TileRow key={step.n} isFirst={i === 0} ringed tile={step.n} eyebrow={step.eyebrow} value={step.value} />
        ))}
      </div>
      <DsButton variant="primary" size="touch" onClick={onDeploy} className="lg:hidden w-full">
        <PlusIcon size={15} />
        Deploy a web app
      </DsButton>
    </SurfaceCard>
  );
};

const AppPlanUsage = (props: { hours: number }) => {
  const { hours } = props;
  const usagePercentage = (hours / 600) * 100;

  let progressBarColour = 'bg-light-blue', hourTextColor = 'text-light-blue';
  if (usagePercentage >= 80 && usagePercentage <= 99) {
    progressBarColour = 'bg-alert-yellow';
    hourTextColor = 'text-alert-yellow';
  } else if (usagePercentage >= 100) {
    progressBarColour = 'bg-error-red'
    hourTextColor = 'text-error-red'
  }

  return <>
    <div className="text-sm mb-[0.375rem] w-full flex gap-6">
      <span>Build + Running hours</span> 
      <span className={`${hourTextColor}`}>{hours} <span className="text-light-grey">/ 600 hours</span></span>
    </div>
    <div className="w-full bg-dark-grey rounded-full h-1.5">
      <div className={`${progressBarColour} h-1.5 rounded-full max-w-full`} style={{ width: `${usagePercentage}%`}}></div>
    </div>
  </>
}

const AppPlanChip = (props: {planName: string}) => {
  const { planName } = props;
  // Rectangular 4px pill, outline only — the plan is state, not an action.
  const tone = planName.includes('Shared') || planName.includes('Dedicated')
    ? 'text-state-ok-soft border-state-ok-soft'
    : 'text-navy-200 border-navy-400';

  return (
    <span className={`flex-none inline-block border rounded-[4px] px-[7px] py-[3px] font-courrier text-ds-105 uppercase leading-none tracking-[0.06em] ${tone}`}>
      {planName}
    </span>
  );
}

export default MyApps;
